import axios from 'axios';
import { API_BASE_URL } from '@/utils/constants';


const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

api.interceptors.request.use((config) => {
  const stored = localStorage.getItem('auth-storage');

  if (stored) {
    const { state } = JSON.parse(stored);

    if (state?.tokens?.access) {
      config.headers.Authorization = `Bearer ${state.tokens.access}`;
    }
  }

  return config;
});

interface QueueItem {
    resolve: (token: string | null) => void;
    reject: (error: unknown) => void;
}

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    console.log('✅ Response:', response.status, response.config.url);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.error('❌ Response Error:', error.response?.status, error.config?.url);

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      console.error('🔴 Refresh token failed, logging out...');

      const { useAuthStore } = await import('@/stores/authStore');
      useAuthStore.getState().clearAuth();

      window.location.href = '/login';

      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    isRefreshing = true;

    const stored = localStorage.getItem('auth-storage');
    if (!stored) {
      isRefreshing = false;
      return Promise.reject(error);
    }

    try {
      const { state } = JSON.parse(stored);
      const refreshToken = state?.tokens?.refresh;

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      console.log('🔄 Refreshing access token...');

      const response = await axios.post(
        `${API_BASE_URL}/api/auth/refresh/`,
        { refresh: refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const { access } = response.data;

      console.log('✅ Token refreshed successfully');

      const { useAuthStore } = await import('@/stores/authStore');
      useAuthStore.getState().updateTokens({
        access,
        refresh: refreshToken
      });

      processQueue(null, access);

      originalRequest.headers.Authorization = `Bearer ${access}`;
      return api(originalRequest);

    } catch (refreshError) {
      console.error('❌ Refresh token failed:', refreshError);

      processQueue(refreshError, null);

      const { useAuthStore } = await import('@/stores/authStore');
      useAuthStore.getState().clearAuth();

      window.location.href = '/login';

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;